import { useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { useAuth } from '@/lib/auth/useAuth'
import { recoveryArrival } from '@/lib/auth/auth'
import { deepLinkArrival } from '@/lib/roads/userLocation'

// The intro (Onboarding) dispatches this when it closes, so the welcome
// popup can queue behind it on a first visit. Uses the same storage key
// the intro uses to decide whether it will show at all.
const ONBOARDING_KEY = 'rr-onboarded-v1'
const INTRO_DONE_EVENT = 'rr-intro-done'
// Manual activation (Find-Me-A-Ride under-construction overlay).
export const WELCOME_OPEN_EVENT = 'rr-open-welcome'

export default function WelcomePopup() {
  const { session, ready } = useAuth()
  const [visible, setVisible] = useState(false)
  // Decided once auth has settled: only signed-out users ever see the popup.
  const [eligible, setEligible] = useState(false)

  useEffect(() => {
    // Recovery-email arrivals go straight to the change-password flow —
    // same reason the intro skips itself.
    if (recoveryArrival || deepLinkArrival) return
    if (!ready) return
    if (session) return
    setEligible(true)
  }, [ready, session])

  // Manual opens bypass the eligibility check on purpose: an explicit click
  // (logged in or not) should always show the popup.
  useEffect(() => {
    const open = () => {
      setEligible(true)
      setVisible(true)
    }
    window.addEventListener(WELCOME_OPEN_EVENT, open)
    return () => window.removeEventListener(WELCOME_OPEN_EVENT, open)
  }, [])

  useEffect(() => {
    if (!eligible) return

    let cancelled = false
    let shown = false
    const show = () => {
      if (!cancelled && !shown) {
        shown = true
        setVisible(true)
      }
    }

    try {
      if (localStorage.getItem(ONBOARDING_KEY)) {
        // Returning user — the intro won't show, so appear right away.
        show()
      } else {
        window.addEventListener(INTRO_DONE_EVENT, show, { once: true })
      }
    } catch {
      show()
    }

    return () => {
      cancelled = true
      window.removeEventListener(INTRO_DONE_EVENT, show)
    }
  }, [eligible])

  if (!eligible) return null

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[58] bg-black/80 backdrop-blur-sm flex items-center justify-center p-6"
          onClick={() => setVisible(false)}
        >
          <motion.div
            initial={{ opacity: 0, y: 14, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 14, scale: 0.97 }}
            transition={{ duration: 0.25 }}
            className="w-full max-w-sm rounded-xl bg-[#0C0C0E]/95 ring-1 ring-amber-400/40 p-6 text-center shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src="/images/roadratings-logo-2.png"
              alt="RoadRatings logo"
              className="h-24 w-auto mx-auto object-contain"
            />

            <div className="mt-5 space-y-3 text-sm leading-relaxed text-white/70">
              <p>Hi,</p>
              <p>
                This is an early stage prototype.
                <br />
                If you notice any bugs, or have feature requests, please send
                them to:{' '}
                <a
                  href="mailto:Rudy@RoadRatings.com"
                  className="text-amber-400 hover:text-amber-300 underline underline-offset-2"
                >
                  Rudy@RoadRatings.com
                </a>
              </p>
              <p>
                Enjoy!
                <br />
                Rudy
              </p>
              <p className="text-xs text-white/50">(Log in to hide this)</p>
            </div>

            <button
              onClick={() => setVisible(false)}
              className="mt-6 h-11 w-full rounded-lg bg-amber-400 hover:bg-amber-300 text-black font-bold text-sm tracking-widest"
            >
              Explore!
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
